import React, { useMemo, useState, useCallback, memo } from 'react';
import { calculateHeatmap } from '@utils/metrics/heatmap.js';
import { formatGrade } from '@utils/gradeUtils.js';
import { customColors } from '@utils/gradeUtils.js';

// Parse hex color to RGB
const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : { r: 0, g: 0, b: 0 };
};

// Get intensity factor based on count and max value
const getIntensity = (count, maxCount = 10) => {
  if (count === 0) return 0;
  // Use dynamic max, with minimum of 10 for consistent color scaling on sparse data
  const effectiveMax = Math.max(maxCount, 10);
  // Scale from 0.3 (light) to 1.0 (full) based on count relative to max
  return 0.3 + (Math.min(count, effectiveMax) / effectiveMax) * 0.7;
};

// Get cell color for flash heatmap based on onsight/flash ratio
const getFlashCellStyle = (count, breakdown, maxCount = 10) => {
  if (count === 0) {
    return { backgroundColor: '#f3f4f6', color: '#9ca3af' }; // gray-100, gray-400
  }

  const { onsight = 0, flash = 0 } = breakdown || {};
  const total = onsight + flash;

  // Calculate flash ratio (0 = all onsight/green, 1 = all flash/orange)
  const flashRatio = total > 0 ? flash / total : 0;

  // Get RGB values for onsight (green) and flash (orange)
  const onsightRgb = hexToRgb(customColors.onsight);
  const flashRgb = hexToRgb(customColors.flash);

  // Blend between onsight and flash colors (mid-tone)
  const blendedR = Math.round(onsightRgb.r + (flashRgb.r - onsightRgb.r) * flashRatio);
  const blendedG = Math.round(onsightRgb.g + (flashRgb.g - onsightRgb.g) * flashRatio);
  const blendedB = Math.round(onsightRgb.b + (flashRgb.b - onsightRgb.b) * flashRatio);

  // Dark versions for high intensity
  const darkBlendedR = Math.round(blendedR * 0.25);
  const darkBlendedG = Math.round(blendedG * 0.25);
  const darkBlendedB = Math.round(blendedB * 0.25);

  // Get intensity (0.3 to 1.0)
  const intensity = getIntensity(count, maxCount);
  const white = 255;

  let r, g, b;
  if (intensity <= 0.65) {
    // Phase 1: white to blended color
    const phase1Progress = (intensity - 0.3) / 0.35;
    r = Math.round(white + (blendedR - white) * phase1Progress);
    g = Math.round(white + (blendedG - white) * phase1Progress);
    b = Math.round(white + (blendedB - white) * phase1Progress);
  } else {
    // Phase 2: blended color to dark version
    const phase2Progress = (intensity - 0.65) / 0.35;
    r = Math.round(blendedR + (darkBlendedR - blendedR) * phase2Progress);
    g = Math.round(blendedG + (darkBlendedG - blendedG) * phase2Progress);
    b = Math.round(blendedB + (darkBlendedB - blendedB) * phase2Progress);
  }

  // Use white text for darker cells (higher intensity)
  const textColor = intensity >= 0.55 ? 'white' : 'inherit';

  return { backgroundColor: `rgb(${r}, ${g}, ${b})`, color: textColor };
};

// Get cell style for clean/attempts heatmaps with dynamic scaling
const getCellStyle = (count, maxCount = 10) => {
  if (count === 0) {
    return { backgroundColor: '#f3f4f6', color: '#9ca3af' }; // gray-100, gray-400
  }

  // Get intensity (0.3 to 1.0)
  const intensity = getIntensity(count, maxCount);

  // Two-phase color interpolation:
  // Phase 1 (intensity 0.3-0.65): white -> green-600
  // Phase 2 (intensity 0.65-1.0): green-600 -> dark green (near black)
  const greenRgb = { r: 22, g: 163, b: 74 };      // green-600
  const darkGreenRgb = { r: 5, g: 46, b: 22 };    // green-950
  const white = 255;

  let r, g, b;
  if (intensity <= 0.65) {
    // Phase 1: white to green
    const phase1Progress = (intensity - 0.3) / 0.35; // 0 to 1
    r = Math.round(white + (greenRgb.r - white) * phase1Progress);
    g = Math.round(white + (greenRgb.g - white) * phase1Progress);
    b = Math.round(white + (greenRgb.b - white) * phase1Progress);
  } else {
    // Phase 2: green to dark green
    const phase2Progress = (intensity - 0.65) / 0.35; // 0 to 1
    r = Math.round(greenRgb.r + (darkGreenRgb.r - greenRgb.r) * phase2Progress);
    g = Math.round(greenRgb.g + (darkGreenRgb.g - greenRgb.g) * phase2Progress);
    b = Math.round(greenRgb.b + (darkGreenRgb.b - greenRgb.b) * phase2Progress);
  }

  // Use white text for darker cells (higher intensity)
  const textColor = intensity >= 0.55 ? 'white' : 'inherit';

  return { backgroundColor: `rgb(${r}, ${g}, ${b})`, color: textColor };
};

const HeatmapChart = memo(({
  data,
  climbingStyle,
  gradeSystem,
  heatmapType = 'clean', // 'flash', 'clean', or 'attempts'
  timePeriod = 'quarterly', // 'monthly', 'quarterly', or 'yearly'
  onCellClick
}) => {
  const [modalData, setModalData] = useState(null);

  // Calculate heatmap data
  const heatmapData = useMemo(() => {
    return calculateHeatmap(data, climbingStyle, timePeriod);
  }, [data, climbingStyle, timePeriod]);

  const { periods, periodLabels, grades, heatmapClean, heatmapFlash, heatmapAttempts, routesClean, routesFlash, routesAttempts, flashBreakdown, cgpByPeriod, cgpPerDayByPeriod } = heatmapData;

  // Select the appropriate heatmap based on type
  const currentHeatmap = useMemo(() => {
    switch (heatmapType) {
      case 'flash': return heatmapFlash;
      case 'attempts': return heatmapAttempts;
      default: return heatmapClean;
    }
  }, [heatmapType, heatmapClean, heatmapFlash, heatmapAttempts]);

  const currentRoutes = useMemo(() => {
    switch (heatmapType) {
      case 'flash': return routesFlash;
      case 'attempts': return routesAttempts;
      default: return routesClean;
    }
  }, [heatmapType, routesClean, routesFlash, routesAttempts]);

  // Calculate max count for dynamic intensity scaling
  const maxCount = useMemo(() => {
    let max = 0;
    for (const grade of grades) {
      for (const period of periods) {
        const count = currentHeatmap[grade]?.[period] || 0;
        if (count > max) max = count;
      }
    }
    return max;
  }, [currentHeatmap, grades, periods]);

  // Get unique years and count periods per year for header grouping
  const { years, periodsPerYear } = useMemo(() => {
    if (timePeriod === 'yearly') return { years: [], periodsPerYear: {} };

    const countByYear = {};
    periods.forEach(p => {
      const year = p.split('-')[0];
      countByYear[year] = (countByYear[year] || 0) + 1;
    });

    return {
      years: Object.keys(countByYear).sort(),
      periodsPerYear: countByYear
    };
  }, [periods, timePeriod]);

  const handleCellClick = useCallback((grade, period) => {
    const routes = currentRoutes[grade]?.[period] || [];
    if (routes.length > 0) {
      setModalData({
        grade,
        period,
        periodLabel: periodLabels[period],
        routes
      });
      if (onCellClick) {
        onCellClick({ grade, period, routes });
      }
    }
  }, [currentRoutes, periodLabels, onCellClick]);

  const closeModal = useCallback(() => {
    setModalData(null);
  }, []);

  const formatGradeLabel = useCallback((grade) => {
    return formatGrade(grade, gradeSystem, climbingStyle);
  }, [gradeSystem, climbingStyle]);

  if (!periods.length || !grades.length) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500">
        No data available for heatmap
      </div>
    );
  }

  // Render legend based on heatmap type
  const renderLegend = () => {
    // Generate legend steps based on max count
    const effectiveMax = Math.max(maxCount, 10);
    const legendSteps = [0];
    const numSteps = 5;
    for (let i = 1; i <= numSteps; i++) {
      legendSteps.push(Math.round((effectiveMax / numSteps) * i));
    }

    if (heatmapType === 'flash') {
      return (
        <div className="flex items-center gap-2 mt-2 text-xs flex-wrap">
          <div className="flex items-center gap-1">
            <div
              className="w-4 h-4 border border-gray-300 rounded-sm"
              style={{ backgroundColor: customColors.onsight }}
            />
            <span>Onsight</span>
          </div>
          <div className="flex items-center gap-1">
            <div
              className="w-4 h-4 border border-gray-300 rounded-sm"
              style={{ backgroundColor: customColors.flash }}
            />
            <span>Flash</span>
          </div>
          <span className="text-gray-400 mx-2">|</span>
          <span className="text-gray-600">Intensity: 1-{effectiveMax}</span>
        </div>
      );
    }

    return (
      <div className="flex items-center gap-1 mt-2 text-xs flex-wrap">
        <span className="text-gray-600 mr-1">Count:</span>
        {legendSteps.map((n, idx) => (
          <div
            key={idx}
            className="w-5 h-4 flex items-center justify-center border border-gray-300 text-[10px]"
            style={getCellStyle(n, effectiveMax)}
          >
            {n}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="h-full flex flex-col">
      {/* Scrollable heatmap container */}
      <div className="flex-1 overflow-auto">
        <table className="border-collapse text-[10px] leading-none">
          <thead>
            {/* Year header row - only for monthly/quarterly */}
            {timePeriod !== 'yearly' && (
              <tr>
                <th className="sticky left-0 top-0 z-20 bg-gray-100 border border-gray-200 px-1 py-0.5"></th>
                {years.map(year => (
                  <th
                    key={year}
                    colSpan={periodsPerYear[year]}
                    className="sticky top-0 z-10 bg-gray-200 border border-gray-200 px-0.5 py-0.5 font-bold text-center"
                  >
                    {year}
                  </th>
                ))}
              </tr>
            )}
            {/* Period header row */}
            <tr>
              <th className={`sticky left-0 ${timePeriod !== 'yearly' ? 'top-4' : 'top-0'} z-20 bg-gray-100 border border-gray-200 px-1 py-0.5 font-semibold`}>

              </th>
              {periods.map(p => (
                <th
                  key={p}
                  className={`sticky ${timePeriod !== 'yearly' ? 'top-4' : 'top-0'} z-10 bg-gray-100 border border-gray-200 px-0.5 py-0.5 font-normal ${timePeriod === 'yearly' ? 'w-8' : 'w-5'}`}
                >
                  {periodLabels[p]}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {grades.map(grade => (
              <tr key={grade}>
                <td className="sticky left-0 z-10 bg-gray-100 border border-gray-200 px-1 py-0.5 font-semibold text-right">
                  {formatGradeLabel(grade)}
                </td>
                {periods.map(period => {
                  const count = currentHeatmap[grade]?.[period] || 0;
                  const hasRoutes = count > 0;
                  const breakdown = flashBreakdown?.[grade]?.[period];

                  // Use blended colors for flash heatmap, green scale otherwise
                  const style = heatmapType === 'flash'
                    ? getFlashCellStyle(count, breakdown, maxCount)
                    : getCellStyle(count, maxCount);

                  return (
                    <td
                      key={`${grade}-${period}`}
                      className={`border border-gray-200 px-0.5 py-0.5 text-center ${timePeriod === 'yearly' ? 'w-8' : 'w-5'} h-4 ${hasRoutes ? 'cursor-pointer hover:ring-1 hover:ring-blue-400' : ''}`}
                      style={style}
                      onClick={() => hasRoutes && handleCellClick(grade, period)}
                      title={hasRoutes ? (heatmapType === 'flash' ? `${breakdown?.onsight || 0} onsight, ${breakdown?.flash || 0} flash` : `${count} ascent${count > 1 ? 's' : ''}`) : ''}
                    >
                      {count > 0 ? count : ''}
                    </td>
                  );
                })}
              </tr>
            ))}
            <tr>
              <td className="sticky left-0 z-10 bg-gray-200 border border-gray-200 px-1 py-0.5 font-semibold text-right">
                CGP
              </td>
              {periods.map(period => {
                const cgp = cgpByPeriod?.[period] || 0;
                return (
                  <td
                    key={`cgp-${period}`}
                    className={`bg-gray-50 border border-gray-200 px-0.5 py-0.5 text-center font-semibold ${timePeriod === 'yearly' ? 'w-8' : 'w-5'} h-4`}
                    title={cgp > 0 ? `CGP: ${cgp.toFixed(2)}` : ''}
                  >
                    {cgp > 0 ? cgp.toFixed(1) : ''}
                  </td>
                );
              })}
            </tr>
            <tr>
              <td className="sticky left-0 z-10 bg-gray-200 border border-gray-200 px-1 py-0.5 font-semibold text-right">
                CGP/day
              </td>
              {periods.map(period => {
                const cgpPerDay = cgpPerDayByPeriod?.[period] || 0;
                return (
                  <td
                    key={`cgppd-${period}`}
                    className={`bg-gray-50 border border-gray-200 px-0.5 py-0.5 text-center font-semibold ${timePeriod === 'yearly' ? 'w-8' : 'w-5'} h-4`}
                    title={cgpPerDay > 0 ? `CGP/day: ${cgpPerDay.toFixed(2)}` : ''}
                  >
                    {cgpPerDay > 0 ? cgpPerDay.toFixed(1) : ''}
                  </td>
                );
              })}
            </tr>
          </tbody>
        </table>
      </div>

      {/* Legend */}
      {renderLegend()}

      {/* Modal for route details */}
      {modalData && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          onClick={closeModal}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex justify-between items-center p-4 border-b">
              <h3 className="text-lg font-semibold">
                Grade {formatGradeLabel(modalData.grade)} - {modalData.periodLabel || modalData.period}
              </h3>
              <button
                onClick={closeModal}
                className="text-gray-500 hover:text-gray-700 text-2xl leading-none"
              >
                &times;
              </button>
            </div>
            <div className="overflow-auto max-h-[60vh] p-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-2">Route</th>
                    <th className="text-left py-2 px-2">Grade</th>
                    <th className="text-left py-2 px-2">Crag</th>
                    <th className="text-left py-2 px-2">Date</th>
                    <th className="text-left py-2 px-2">Type</th>
                  </tr>
                </thead>
                <tbody>
                  {modalData.routes.map((route, idx) => (
                    <tr key={idx} className="border-b hover:bg-gray-50">
                      <td className="py-2 px-2">{route.name}</td>
                      <td className="py-2 px-2 text-gray-600">{route.grade}</td>
                      <td className="py-2 px-2 text-gray-600">{route.crag}</td>
                      <td className="py-2 px-2 text-gray-600">{route.date}</td>
                      <td className="py-2 px-2">{route.type}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

HeatmapChart.displayName = 'HeatmapChart';

export default HeatmapChart;
